import { useMemo, useEffect } from 'react';
import { useWatch, useFormContext, Widget } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';
import { EmploymentDetails } from '@kanda-libs/ks-frontend-services';
import get from 'lodash.get';

import { getFieldWidgetTag, generateYearsOptions, getTime } from './helpers';
import { ERROR_PROPS, TITLES } from './constants';

const checkInPast = (
  values: StringIndexedObject,
  status: EmploymentDetails['employment_status'],
) => {
  const rootName = `credit.employment_details.${status}_details`;
  const names = {
    months: `${rootName}.month_started_${status}`,
    years: `${rootName}.year_started_${status}`,
  };
  const times = get(values, rootName);
  const years = times?.[names.years] || -1;
  const months = times?.[names.months] || -1;
  const time = getTime(years, months);
  return time >= 0;
};

export default function useYearsField(
  status: EmploymentDetails['employment_status'],
) {
  const { getValues, setValue, formState, trigger } = useFormContext();

  const rootName = `credit.employment_details.${status}_details`;

  const names = {
    months: `${rootName}.month_started_${status}`,
    years: `${rootName}.year_started_${status}`,
  };

  const [years, months] = useWatch({
    name: [names.years, names.months],
  });

  const widgetName = getFieldWidgetTag(status);

  const validation = {
    ...Widget[`${widgetName}Validation`],
    required: {
      value: true,
      message: `You must provide the date you moved became ${status
        .split('_')
        .join(' ')}`,
    },
    validate: {
      value: {
        inPast: () => checkInPast(getValues(), status),
      },
      message: {
        inPast: 'The time provided must be in the past',
      },
    },
  };

  const title = useMemo(() => {
    if (!status) return '';
    if (status === 'employed') return TITLES.EMPLOYED;
    if (status === 'self_employed') return TITLES.SELF_EMPLOYED;
    if (status === 'not_employed') return TITLES.NOT_EMPLOYED;
    return TITLES.RETIRED;
  }, [status]);

  const fieldName = `credit.employment_details.${status}_details.months_${status}`;

  const error = useMemo(() => {
    if (years && months) {
      const time = getTime(years, months);
      if (time < 0) return 'The time provided must be in the past';
    }
    const heldError = get(formState?.errors, fieldName);
    if (!heldError) return null;
    if (heldError?.type === 'required')
      return ERROR_PROPS.label.required.replace(
        '$STATUS',
        status.split('_').join(' '),
      );
    if (heldError?.type === 'inPast') return ERROR_PROPS.label.inPast;
    return 'There is an error with this input';
  }, [formState, fieldName, years, months, status]);

  const yearsOptions = generateYearsOptions();

  const FieldWidget = useMemo(() => {
    if (!status) return undefined;
    if (status === 'employed')
      return Widget.CreditEmploymentDetailsEmployedDetailsMonthsEmployed;
    if (status === 'self_employed')
      return Widget.CreditEmploymentDetailsSelfEmployedDetailsMonthsSelfEmployed;
    if (status === 'not_employed')
      return Widget.CreditEmploymentDetailsNotEmployedDetailsMonthsNotEmployed;
    if (status === 'retired')
      return Widget.CreditEmploymentDetailsRetiredDetailsMonthsRetired;
    return undefined;
  }, [status]);

  // Effect unsets value if certain conditions met
  useEffect(() => {
    if (!years || !months) {
      setValue(fieldName, undefined);
      return;
    }
    const time = getTime(years, months);
    if (time < 0) {
      setValue(fieldName, undefined);
      return;
    }
    setValue(fieldName, time);
    trigger(fieldName);
  }, [years, months, setValue, fieldName, trigger]);

  // Effect unsets value if error is present
  useEffect(() => {
    if (!error) return;
    setValue(fieldName, undefined);
  }, [error, fieldName, setValue]);

  return {
    title,
    names,
    validation,
    error,
    yearsOptions,
    FieldWidget,
  };
}
